import { clamp, median } from '~/lib/utils';
import type { Frame } from '~/types/pipeline';

export type NormalizedPreviewCanvas = {
  w: number;
  h: number;
  dx: number;
  dy: number;
};

const CLAMP_MULTIPLIER = 4;

/**
 * Compute a shared preview canvas for all frames so thumbnails render WYSIWYG
 * with exported pack geometry.
 */
export function getNormalizedPreviewCanvas(frames: readonly Frame[]): NormalizedPreviewCanvas {
  if (frames.length === 0) {
    return { w: 0, h: 0, dx: 0, dy: 0 };
  }

  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  const sizes = new Array<number>(frames.length);
  for (let i = 0; i < frames.length; i++) {
    const f = frames[i] as Frame;
    const left = f.spriteSourceSize.x;
    const top = f.spriteSourceSize.y;
    const right = Math.max(f.sourceSize.w, left + f.trimRect.w);
    const bottom = Math.max(f.sourceSize.h, top + f.trimRect.h);
    if (left < minX) minX = left;
    if (top < minY) minY = top;
    if (right > maxX) maxX = right;
    if (bottom > maxY) maxY = bottom;
    sizes[i] = Math.max(f.sourceSize.w, f.sourceSize.h);
  }

  let w = maxX - minX;
  let h = maxY - minY;
  let dx = minX ? -minX : 0;
  let dy = minY ? -minY : 0;

  const medianSize = median(sizes);
  const maxAllowed = medianSize * CLAMP_MULTIPLIER;
  const clampedX = medianSize > 0 && w > maxAllowed;
  const clampedY = medianSize > 0 && h > maxAllowed;
  if (clampedX || clampedY) {
    console.warn('[normalizedCanvas] clamping degenerate canvas', {
      canvas: { w, h },
      maxAllowed,
    });
    if (clampedX) {
      w = maxAllowed;
      // Reproject offsets after clamp so sane near-origin frames stay visible
      // when one outlier frame expands bounds far into negative/positive space.
      dx = clamp(w - maxX, -minX, 0);
    }
    if (clampedY) {
      h = maxAllowed;
      dy = clamp(h - maxY, -minY, 0);
    }
  }

  return { w, h, dx, dy };
}
